import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface FinancialYearOption {
  label: string; // e.g. "26-27"
  fullLabel?: string; // e.g. "Financial Year 26-27"
  startYear: number; // e.g. 2026
  endYear: number; // e.g. 2027
  typeIndex: number; // e.g. 2 (current), 1 (prev), 0 (prev-prev)
  isCurrent: boolean;
  status: 'Current' | 'Closed' | string;
  value?: any;
}

@Component({
  selector: 'app-financial-year-filter',
  templateUrl: './app-financial-year-filter.component.html',
  styleUrls: ['./app-financial-year-filter.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppFinancialYearFilterComponent),
      multi: true,
    },
  ],
})
export class AppFinancialYearFilterComponent implements OnInit, ControlValueAccessor {
  /** Selected year: can be start year number (e.g. 2026), type index (2, 1, 0), or label string */
  @Input() selectedYear: any = null;

  /** Number of consecutive financial years to generate (default 3) */
  @Input() yearsCount: number = 3;

  /** Base FY start year (defaults to Indian FY calculation from current date) */
  @Input() currentYearStart: number | null = null;

  /** Custom options list if overridden */
  @Input() customOptions: FinancialYearOption[] | null = null;

  /** Prefix text for the button (default: "Financial Year") */
  @Input() prefixText: string = 'Financial Year';

  /** Whether to show the archived link at bottom */
  @Input() showArchivedLink: boolean = true;

  /** Archived link text */
  @Input() archivedText: string = 'Archived Years';

  /** Disabled state */
  @Input() disabled: boolean = false;

  /** Optional custom container CSS class */
  @Input() customClass: string = '';

  /** Emitted with the typeIndex (e.g. 2 for current, 1 for prev, 0 for prev-prev) for backwards compatibility */
  @Output() yearSelect = new EventEmitter<number>();

  /** Emitted with the start year number (e.g. 2026, 2025, 2024) */
  @Output() startYearSelect = new EventEmitter<number>();

  /** Emitted with full details of selected financial year */
  @Output() yearChange = new EventEmitter<FinancialYearOption>();

  /** Emitted when the archived years link is clicked */
  @Output() archivedClick = new EventEmitter<void>();

  isOpen: boolean = false;
  options: FinancialYearOption[] = [];

  private onChange: (val: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private elementRef: ElementRef) {}

  ngOnInit(): void {
    this.initOptions();
  }

  ngOnChanges(): void {
    this.initOptions();
  }

  private initOptions(): void {
    if (this.customOptions && this.customOptions.length > 0) {
      this.options = this.customOptions;
      return;
    }

    const today = new Date();
    const computedStart =
      this.currentYearStart ??
      (today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1);

    const generated: FinancialYearOption[] = [];
    const count = Math.max(1, this.yearsCount || 3);

    // Generate in descending order: current (offset 0), prev (offset -1), prev-prev (offset -2)...
    for (let i = 0; i < count; i++) {
      const startYear = computedStart - i;
      const endYear = startYear + 1;
      const label = `${String(startYear).slice(-2)}-${String(endYear).slice(-2)}`;
      const typeIndex = count - 1 - i; // For 3 items: 2 (current), 1 (prev), 0 (prev-prev)
      const isCurrent = i === 0;

      generated.push({
        label,
        fullLabel: `${this.prefixText} ${label}`,
        startYear,
        endYear,
        typeIndex,
        isCurrent,
        status: isCurrent ? 'Current' : 'Closed',
        value: startYear,
      });
    }

    this.options = generated;
  }

  get selectedOption(): FinancialYearOption | undefined {
    if (this.selectedYear === null || this.selectedYear === undefined) {
      return this.options[0];
    }

    return this.options.find(
      (opt) =>
        opt.startYear === this.selectedYear ||
        opt.startYear === Number(this.selectedYear) ||
        opt.typeIndex === this.selectedYear ||
        opt.typeIndex === Number(this.selectedYear) ||
        opt.label === String(this.selectedYear) ||
        opt.value === this.selectedYear
    );
  }

  get displayLabel(): string {
    const selected = this.selectedOption;
    if (selected) {
      return `${this.prefixText} ${selected.label}`;
    }
    if (typeof this.selectedYear === 'number' && this.selectedYear > 1900) {
      const end = this.selectedYear + 1;
      return `${this.prefixText} ${String(this.selectedYear).slice(-2)}-${String(end).slice(-2)}`;
    }
    if (this.options.length > 0) {
      return `${this.prefixText} ${this.options[0].label}`;
    }
    return this.prefixText;
  }

  isSelected(opt: FinancialYearOption): boolean {
    const selected = this.selectedOption;
    return selected ? selected.startYear === opt.startYear : false;
  }

  toggleDropdown(event?: Event): void {
    if (this.disabled) return;
    if (event) event.stopPropagation();
    this.isOpen = !this.isOpen;
    if (!this.isOpen) {
      this.onTouched();
    }
  }

  closeDropdown(): void {
    if (this.isOpen) {
      this.isOpen = false;
      this.onTouched();
    }
  }

  selectOption(opt: FinancialYearOption, event?: Event): void {
    if (event) event.stopPropagation();
    this.selectedYear = opt.startYear;
    this.onChange(opt.startYear);
    this.startYearSelect.emit(opt.startYear);
    this.yearSelect.emit(opt.typeIndex);
    this.yearChange.emit(opt);
    this.closeDropdown();
  }

  onArchivedClick(event: Event): void {
    event.stopPropagation();
    this.archivedClick.emit();
    this.closeDropdown();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      this.isOpen &&
      this.elementRef &&
      !this.elementRef.nativeElement.contains(event.target)
    ) {
      this.closeDropdown();
    }
  }

  @HostListener('keydown.escape')
  onEscape(): void {
    this.closeDropdown();
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    this.selectedYear = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
